/* eslint-disable react-doctor/js-combine-iterations, react-doctor/js-flatmap-filter */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SUPPORTED_COUNTRY_CODES } from "@wags/geocoding/supported-countries.ts";
import { toast } from "@wags/ui/sonner.tsx";
import { useState } from "react";
import { serverGeocodeAddress, serverReverseGeocode } from "./geocoding";

const GOOGLE_API_KEY =
    (import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined) ?? "";

const PLACES_AUTOCOMPLETE_ENDPOINT =
    "https://places.googleapis.com/v1/places:autocomplete";
const PLACES_NEARBY_SEARCH_ENDPOINT =
    "https://places.googleapis.com/v1/places:searchNearby";

const AUTOCOMPLETE_STALE_TIME = 1000 * 60 * 5;
const NEARBY_STALE_TIME = 1000 * 60 * 5;

type AutocompleteMode = "address" | "city";

const getAutocompleteQueryKey = (input: string, mode: AutocompleteMode) => [
    "google-places",
    "autocomplete",
    mode,
    input.toLowerCase(),
];

const getNearbyCitiesQueryKey = (lat?: number, lng?: number) => [
    "google-places",
    "nearby",
    lat ?? null,
    lng ?? null,
];

const getLocationErrorMessage = (error?: GeolocationPositionError) => {
    if (!error) {
        return "Unable to get your location. Please try again.";
    }

    switch (error.code) {
        case error.PERMISSION_DENIED:
            return "Location permission denied. Please enable it to continue.";
        case error.POSITION_UNAVAILABLE:
            return "Location unavailable. Please try again.";
        case error.TIMEOUT:
            return "Location request timed out. Please try again.";
        default:
            return "Unable to get your location. Please try again.";
    }
};

type Location = {
    lat: number;
    lng: number;
};

type LocationWithDisplay = Location & {
    address?: {
        line1: string;
        line2: string;
        city: string;
        region: string;
        postalCode: string;
        country: string;
    };
    displayName: string;

    state?: string;
};
type PlaceResult = {
    description: string;
    place_id: string;
};

type PlacePredictionAPI = {
    placePrediction?: {
        placeId: string;
        text?: { text?: string };
        structuredFormat?: {
            mainText?: { text?: string };
            secondaryText?: { text?: string };
        };
    };
};

type AutocompleteSuggestionResponse = {
    suggestions: PlacePredictionAPI[];
};

type NearbySearchResponse = {
    places?: Array<{
        id?: string;
        displayName?: { text?: string };
        formattedAddress?: string;
    }>;
};

type NearbySearchOptions = {
    radiusMeters?: number;
    maxResults?: number;
};

async function fetchAutocompleteSuggestions(
    input: string,
    mode: AutocompleteMode = "city",
): Promise<PlaceResult[]> {
    const trimmedInput = input.trim();
    if (!trimmedInput || trimmedInput.length < 3) {
        return [];
    }

    if (!GOOGLE_API_KEY) {
        console.warn("Google Places API key not configured");
        return [];
    }

    try {
        const body = {
            input: trimmedInput,
            includedRegionCodes: SUPPORTED_COUNTRY_CODES.map((code) =>
                code.toLowerCase(),
            ),
            ...(mode === "city" ? { includedPrimaryTypes: ["(cities)"] } : {}),
            languageCode: "en",
        };

        const res = await fetch(PLACES_AUTOCOMPLETE_ENDPOINT, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Goog-Api-Key": GOOGLE_API_KEY,
                "X-Goog-FieldMask":
                    "suggestions.placePrediction.placeId,suggestions.placePrediction.structuredFormat",
            },
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            console.error("Autocomplete error", await res.text());
            return [];
        }

        const data = (await res.json()) as AutocompleteSuggestionResponse;
        return (data.suggestions ?? [])
            .map((s) => s.placePrediction)
            .filter(Boolean)
            .map((p) => {
                const main =
                    p?.structuredFormat?.mainText?.text || p?.text?.text || "";
                const secondary =
                    p?.structuredFormat?.secondaryText?.text || "";
                return {
                    place_id: p?.placeId || "",
                    description: secondary ? `${main}, ${secondary}` : main,
                };
            });
    } catch (error) {
        console.error("Error fetching suggestions:", error);
        return [];
    }
}

async function fetchNearbyCities(
    lat: number,
    lng: number,
    options: NearbySearchOptions = {},
): Promise<PlaceResult[]> {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return [];
    }

    if (!GOOGLE_API_KEY) {
        console.warn("Google Places API key not configured");
        return [];
    }

    const { radiusMeters = 50_000, maxResults = 3 } = options;

    try {
        const res = await fetch(PLACES_NEARBY_SEARCH_ENDPOINT, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Goog-Api-Key": GOOGLE_API_KEY,
                "X-Goog-FieldMask":
                    "places.id,places.displayName,places.formattedAddress",
            },
            body: JSON.stringify({
                includedTypes: ["locality"],
                maxResultCount: maxResults,
                languageCode: "en",
                locationRestriction: {
                    circle: {
                        center: {
                            latitude: lat,
                            longitude: lng,
                        },
                        radius: radiusMeters,
                    },
                },
            }),
        });

        if (!res.ok) {
            console.error("Nearby search error", await res.text());
            return [];
        }

        const data = (await res.json()) as NearbySearchResponse;
        const mapped = (data.places ?? [])
            .map((place) => {
                const description =
                    place.formattedAddress || place.displayName?.text || "";
                return {
                    place_id: place.id ?? "",
                    description,
                };
            })
            .filter((place) => place.place_id && place.description);

        const unique = new Map<string, PlaceResult>();
        for (const place of mapped) {
            if (!unique.has(place.description)) {
                unique.set(place.description, place);
            }
        }

        return Array.from(unique.values()).slice(0, maxResults);
    } catch (error) {
        console.error("Error fetching nearby cities:", error);
        return [];
    }
}

type GooglePlacesOptions = {
    autocompleteInput?: string;
    autocompleteEnabled?: boolean;
    autocompleteMode?: AutocompleteMode;
    nearbyLat?: number;
    nearbyLng?: number;
    nearbyEnabled?: boolean;
    prefetchInput?: string;
};

async function reverseGeocodeLocation(
    lat: number,
    lng: number,
): Promise<LocationWithDisplay | null> {
    try {
        const result = await serverReverseGeocode({ data: { lat, lng } });
        if (!result) {
            return null;
        }
        return {
            lat: result.lat,
            lng: result.lng,
            displayName: result.displayName,
            state: result.state,
        };
    } catch (error) {
        console.error("Error reverse geocoding:", error);
        return null;
    }
}

async function geocodeAddressInput(
    input: string,
): Promise<LocationWithDisplay | null> {
    const trimmedInput = input.trim();
    if (!trimmedInput) {
        return null;
    }

    try {
        const result = await serverGeocodeAddress({
            data: { address: trimmedInput },
        });
        if (!result) {
            return null;
        }
        return {
            lat: result.lat,
            lng: result.lng,
            displayName: result.displayName || trimmedInput,
        };
    } catch (error) {
        console.error("Error geocoding address input:", error);
        return null;
    }
}

type GoogleAddressComponent = {
    types?: string[];
    longText?: string;
    shortText?: string;
};

type GoogleAddressParts = {
    city: string;
    country: string;
    line2: string;
    postalCode: string;
    route: string;
    state: string;
    streetNumber: string;
};

function applyPrimaryAddressComponent(
    parts: GoogleAddressParts,
    component: GoogleAddressComponent,
) {
    if (component.types?.includes("locality") && component.longText) {
        parts.city = component.longText;
    }
    if (
        component.types?.includes("postal_town") &&
        component.longText &&
        !parts.city
    ) {
        parts.city = component.longText;
    }
    if (
        component.types?.includes("administrative_area_level_1") &&
        component.shortText
    ) {
        parts.state = component.shortText;
    }
    if (component.types?.includes("street_number") && component.longText) {
        parts.streetNumber = component.longText;
    }
}

function applySecondaryAddressComponent(
    parts: GoogleAddressParts,
    component: GoogleAddressComponent,
) {
    if (component.types?.includes("route") && component.longText) {
        parts.route = component.longText;
    }
    if (component.types?.includes("subpremise") && component.longText) {
        parts.line2 = component.longText;
    }
    if (component.types?.includes("postal_code") && component.longText) {
        parts.postalCode = component.longText;
    }
    if (component.types?.includes("country") && component.shortText) {
        parts.country = component.shortText;
    }
}

export function useGooglePlaces(options: GooglePlacesOptions = {}) {
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const trimmedAutocompleteInput = (options.autocompleteInput ?? "").trim();
    const normalizedAutocompleteInput = trimmedAutocompleteInput.toLowerCase();
    const autocompleteMode = options.autocompleteMode ?? "city";
    const shouldFetchAutocomplete =
        (options.autocompleteEnabled ?? true) &&
        normalizedAutocompleteInput.length >= 3;
    const shouldFetchNearby =
        (options.nearbyEnabled ?? true) &&
        options.nearbyLat != null &&
        options.nearbyLng != null;

    const {
        data: autocompleteSuggestions,
        isFetching: autocompleteIsFetching,
    } = useQuery({
        queryKey: getAutocompleteQueryKey(
            normalizedAutocompleteInput,
            autocompleteMode,
        ),
        queryFn: () =>
            fetchAutocompleteSuggestions(
                trimmedAutocompleteInput,
                autocompleteMode,
            ),
        enabled: shouldFetchAutocomplete,
        staleTime: AUTOCOMPLETE_STALE_TIME,
    });

    const { data: nearbySuggestions, isFetching: nearbyIsFetching } = useQuery({
        queryKey: getNearbyCitiesQueryKey(options.nearbyLat, options.nearbyLng),
        queryFn: () =>
            fetchNearbyCities(options.nearbyLat ?? 0, options.nearbyLng ?? 0),
        enabled: shouldFetchNearby,
        staleTime: NEARBY_STALE_TIME,
    });

    const prefetchInput = (options.prefetchInput ?? "").trim();
    const normalizedPrefetchInput = prefetchInput.toLowerCase();

    useQuery({
        queryKey: [
            "prefetch-autocomplete",
            normalizedPrefetchInput,
            autocompleteMode,
        ],
        queryFn: async () => {
            await queryClient.prefetchQuery({
                queryKey: getAutocompleteQueryKey(
                    normalizedPrefetchInput,
                    autocompleteMode,
                ),
                queryFn: () =>
                    fetchAutocompleteSuggestions(
                        prefetchInput,
                        autocompleteMode,
                    ),
                staleTime: AUTOCOMPLETE_STALE_TIME,
            });
            return null;
        },
        enabled: normalizedPrefetchInput.length >= 3,
        staleTime: AUTOCOMPLETE_STALE_TIME,
    });

    const getPlaceDetails = async (
        placeId: string,
    ): Promise<LocationWithDisplay | null> => {
        if (!placeId) return null;

        setIsLoading(true);

        if (!GOOGLE_API_KEY) {
            console.error("Google Places API key not configured");
            setIsLoading(false);
            return null;
        }

        let location: LocationWithDisplay | null = null;
        try {
            const resource = placeId.startsWith("places/")
                ? placeId
                : `places/${placeId}`;
            const url = `https://places.googleapis.com/v1/${resource}`;

            const res = await fetch(url, {
                headers: {
                    "X-Goog-Api-Key": GOOGLE_API_KEY,
                    "X-Goog-FieldMask":
                        "id,formattedAddress,location,addressComponents",
                },
            });

            if (!res.ok) {
                console.error("Place details error", await res.text());
            } else {
                const details = await res.json();
                const { location: loc, addressComponents } = details;

                const addressParts: GoogleAddressParts = {
                    city: "",
                    country: "",
                    line2: "",
                    postalCode: "",
                    route: "",
                    state: "",
                    streetNumber: "",
                };

                addressComponents?.forEach(
                    (component: GoogleAddressComponent) => {
                        applyPrimaryAddressComponent(addressParts, component);
                        applySecondaryAddressComponent(addressParts, component);
                    },
                );

                if (!loc) {
                    console.error("No location data in place details");
                } else {
                    const {
                        city,
                        country,
                        line2,
                        postalCode,
                        route,
                        state,
                        streetNumber,
                    } = addressParts;
                    const displayName =
                        city && state
                            ? `${city}, ${state}`
                            : city || state || "";

                    location = {
                        lat: loc.latitude,
                        lng: loc.longitude,
                        displayName,
                        address: {
                            line1: [streetNumber, route]
                                .filter(Boolean)
                                .join(" "),
                            line2,
                            city,
                            region: state,
                            postalCode,
                            country,
                        },
                    };
                }
            }
        } catch (error) {
            console.error("Error getting location details:", error);
        }
        setIsLoading(false);
        return location;
    };

    const getCurrentLocation =
        async (): Promise<LocationWithDisplay | null> => {
            setIsLoading(true);

            return new Promise((resolve) => {
                if ("geolocation" in navigator) {
                    navigator.geolocation.getCurrentPosition(
                        async (position) => {
                            try {
                                const result = await reverseGeocodeLocation(
                                    position.coords.latitude,
                                    position.coords.longitude,
                                );

                                resolve(result);
                            } catch (error) {
                                console.error(
                                    "Error getting location details:",
                                    error,
                                );
                                resolve(null);
                            }
                            setIsLoading(false);
                        },
                        (error) => {
                            console.error("Error getting location:", error);
                            toast.error(getLocationErrorMessage(error));
                            setIsLoading(false);
                            resolve(null);
                        },
                    );
                } else {
                    setIsLoading(false);
                    toast.error(
                        "Geolocation is not supported by your browser.",
                    );
                    resolve(null);
                }
            });
        };

    const isFetching = autocompleteIsFetching || nearbyIsFetching;

    return {
        suggestions: shouldFetchAutocomplete
            ? (autocompleteSuggestions ?? [])
            : [],
        nearbySuggestions: shouldFetchNearby ? (nearbySuggestions ?? []) : [],
        isLoading: isLoading || isFetching,
        getPlaceDetails,
        getAddressDetails: async (placeId: string) =>
            (await getPlaceDetails(placeId))?.address ?? null,
        reverseGeocode: reverseGeocodeLocation,
        geocodeAddress: geocodeAddressInput,
        getCurrentLocation,
    };
}
